import { useCallback, useState } from "react";
import { ISupplement } from "@/types/supplement";
import { memberSupplementService } from "@/services/member-supplement.service";

export function useMemberSupplements(memberId: string) {
  const [supplements, setSupplements] = useState<ISupplement[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);

  const fetchSupplements = useCallback(async () => {
    setLoading(true);
    try {
      const sups = await memberSupplementService.getAllForMember(memberId);
      // Support for paginated or array response
      setSupplements(Array.isArray(sups) ? sups : sups?.data ?? []);
      setFetched(true);
    } finally {
      setLoading(false);
    }
  }, [memberId]);

  const addSupplement = useCallback(
    async (sup: ISupplement | ISupplement[]) => {
      setLoading(true);
      try {
        const sups = await memberSupplementService.createForMember(
          memberId,
          sup
        );
        setSupplements(sups);
      } finally {
        setLoading(false);
      }
    },
    [memberId]
  );

  const updateSupplement = useCallback(
    async (supplementId: string, sup: ISupplement) => {
      setLoading(true);
      try {
        const sups = await memberSupplementService.updateForMember(
          memberId,
          supplementId,
          sup
        );
        setSupplements(sups);
      } finally {
        setLoading(false);
      }
    },
    [memberId]
  );

  const deleteSupplement = useCallback(
    async (supplementId: string) => {
      setLoading(true);
      try {
        const sups = await memberSupplementService.deleteForMember(
          memberId,
          supplementId
        );
        setSupplements(sups);
      } finally {
        setLoading(false);
      }
    },
    [memberId]
  );

  return {
    supplements,
    loading,
    fetched,
    setFetched,
    fetchSupplements,
    addSupplement,
    updateSupplement,
    deleteSupplement,
  };
}
